import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { ToastContainer } from "react-toastify";
import axios from "./api/axios";
import { useLocation } from 'react-router-dom';
import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "./contexts/AuthContext"
import { refreshTokenRoute } from "./api/routes";
import MainSiteContainer from "./containers/MainSiteContainer/MainSiteContainer";
import { pullInAccountSettings } from "./helpers/AccountTypes";
import { toast } from "react-toastify";
import { TOP_LEVEL_NAV_ROUTES } from "./config/NavRoutes";
import RightClickMenu from "./components/RightClickMenu/RightClickMenu";

const overlaySegments = ['obs-feature', 'feature', 'disruptor', 'csl'];

const isOverlayPathname = (pathname = '') => {
  const pathSegment = (pathname.split('/')[1] || '').toLowerCase();
  const featureSegment = (TOP_LEVEL_NAV_ROUTES.FEATURE.split('/')[1] || '').toLowerCase();
  return overlaySegments.includes(pathSegment) || pathSegment === featureSegment;
};

// Location debugger component
const LocationDebugger = () => {
  const location = useLocation();
  
  useEffect(() => {
    console.log('Current location:', location);
    // Add a visible indicator on the page
    const debugElement = document.createElement('div');
    debugElement.style = 'position: fixed; bottom: 10px; right: 10px; background: rgba(0,0,0,0.7); color: white; padding: 10px; z-index: 9999; font-size: 14px; border-radius: 4px;';
    debugElement.textContent = `Path: ${location.pathname}${location.search || ''}`;
    document.body.appendChild(debugElement);
    
    return () => {
      if (document.body.contains(debugElement)) {
        document.body.removeChild(debugElement);
      }
    };
  }, [location]);
  
  return null;
};

function App() {
  const [authContext, setAuthContext] = useContext(AuthContext)
  const location = useLocation();
  const [pulledInConfigs, setPulledInConfigs] = useState(false);

  const verifyTimeout = useRef(null);
  const prevDarkModeRef = useRef(undefined);
  const themeTransitionTimer = useRef(null);

  //Handle refreshing of page to see if the token is still fresh and the user is still authenticated. This is designed to handle when the user refreshes the page
  const verifyUser = useCallback(() => {
    axios
    .post(refreshTokenRoute, {
    },{  withCredentials:true})
    .then(function (response) {
      //console.log('FAILED', response.failed)
      const data = response.data
      setAuthContext((oldValues) => {
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        return { ...oldValues,initialising: false, token:data.token, isConfirmed:data.globalUserData?.isConfirmed ?? data.isConfirmed, isLoggedIn:true, userData:data.globalUserData}
      })
    })
    .catch(function (error) {
      // Treat overlay/preview contexts as unauthenticated-safe pages.
      const isOverlayPath = isOverlayPathname(location.pathname);

      // We only need to respond to the auth error if we are not on an overlay/feature page
      if (!isOverlayPath) {
        // Your existing login failure code
        const params = new URLSearchParams(window.location.search) 
        let twitchFail = params.get('failedTwitchLogin') 
        params.delete('failedTwitchLogin')
        window.history.replaceState(null, '', '?' + params + window.location.hash)

        if(twitchFail) {
          delete axios.defaults.headers.common["Authorization"];
          toast.error("Error: " + error);
        }

        
        // Only reset auth context for non-feature pages
        setAuthContext(oldValues => {
          return { ...oldValues, initialising: false, token: null, isConfirmed:false, isLoggedIn:false, userData:{} }
        });
      } else {
        // For overlay/feature paths, allow preview mode to continue without authentication
        setAuthContext(oldValues => {
          return { ...oldValues, initialising: false, isPreviewMode: true }
        })
      }
    });
    clearTimeout(verifyTimeout.current);
    verifyTimeout.current = setTimeout(verifyUser, 5 * 60 * 1000)
  }, [setAuthContext])

  useEffect(() => {
    setTimeout(verifyUser, 20);
    // Remove no-transitions class after initial render
    setTimeout(() => {
      document.documentElement.classList.remove('no-transitions');
    }, 50);
  }, [verifyUser])

  useEffect(() => {
    const isOverlayPath = isOverlayPathname(location.pathname);
    const rootElement = document.getElementById('root');

    document.documentElement.classList.toggle('overlay-route', isOverlayPath);
    document.body.classList.toggle('overlay-route', isOverlayPath);
    rootElement?.classList.toggle('overlay-route', isOverlayPath);

    return () => {
      document.documentElement.classList.remove('overlay-route');
      document.body.classList.remove('overlay-route');
      rootElement?.classList.remove('overlay-route');
    };
  }, [location.pathname]);

  useEffect(() => {
    if (pulledInConfigs === false) {
      pullInAccountSettings(1000, true);
      setPulledInConfigs(true);
    }
  }, [pulledInConfigs]);

  // Apply dark mode theme based on user preference
  useEffect(() => {
    const htmlElement = document.documentElement;
    const userPref = authContext.userData?.useDarkMode;

    // Snap the theme instantly (no cross-fades) then re-enable transitions
    const applyTheme = (isDark) => {
      htmlElement.classList.add('no-transitions');
      if (isDark) {
        htmlElement.setAttribute('data-theme', 'dark');
        localStorage.setItem('icast-dark-mode', 'true');
        try { document.cookie = `icast-dark-mode=true;path=/;max-age=${60 * 60 * 24 * 365}`; } catch (e) {}
      } else {
        htmlElement.removeAttribute('data-theme');
        localStorage.setItem('icast-dark-mode', 'false');
        try { document.cookie = `icast-dark-mode=false;path=/;max-age=${60 * 60 * 24 * 365}`; } catch (e) {}
      }
      htmlElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      requestAnimationFrame(() => htmlElement.classList.remove('no-transitions'));
    };

    const isActualToggle = prevDarkModeRef.current !== undefined
      && typeof userPref === 'boolean'
      && userPref !== prevDarkModeRef.current;
    prevDarkModeRef.current = userPref;

    if (isActualToggle) {
      // Delay the theme snap until the toggle animation finishes (500 ms)
      if (themeTransitionTimer.current) clearTimeout(themeTransitionTimer.current);
      themeTransitionTimer.current = setTimeout(() => applyTheme(userPref), 500);
    } else if (typeof userPref === 'boolean') {
      // Initial load with a known preference — apply immediately
      applyTheme(userPref);
    } else {
      // No explicit user preference (e.g. logged out) — fall back to cookie/localStorage
      try {
        const cookieMatch = document.cookie.match(/(?:^|; )icast-dark-mode=([^;]+)/);
        const cookieVal = cookieMatch ? cookieMatch[1] : null;
        const saved = cookieVal ?? localStorage.getItem('icast-dark-mode');
        if (saved === 'true') {
          applyTheme(true);
        } else if (saved === 'false') {
          applyTheme(false);
        }
      } catch (e) {
        // ignore
      }
    }
  }, [authContext.userData]);

  return (
      <div className="App">
        {/* <LocationDebugger /> {/* Add the debugger component here */}
        <MainSiteContainer></MainSiteContainer>
        <ToastContainer
          position="bottom-left"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <RightClickMenu />
      </div>
  );
}

export default App;
